import { SandboxDomainError } from '../errors';
import { SandboxHost, SandboxState } from '../types';

export type SandboxHostInput = {
  group_id?: unknown;
  alias?: unknown;
  type?: unknown;
  position?: unknown;
  coord_x?: unknown;
  coord_y?: unknown;
};

export const getHost = (state: SandboxState, id: number): SandboxHost => {
  const host = state.hosts.find((item) => item.id === id);
  if (!host) throw new SandboxDomainError('Хост не найден');
  return host;
};

export const setServiceModeAction = (
  state: SandboxState,
  id: number,
  flag: boolean
): SandboxHost => {
  const host = getHost(state, id);
  if (
    flag &&
    state.sessions.some(
      (session) => session.host_id === id && session.status === 'ACTIVE'
    )
  ) {
    throw new SandboxDomainError(
      'Нельзя перевести занятый хост в сервисный режим'
    );
  }
  host.in_service = flag;
  return host;
};

export const setShellClientAction = (
  state: SandboxState,
  id: number,
  clientUuid: string | null
): SandboxHost => {
  const host = getHost(state, id);

  if (
    clientUuid &&
    !state.clients.some((client) => client.uuid === clientUuid)
  ) {
    throw new SandboxDomainError('Клиент не найден');
  }

  host.shell_client_uuid = clientUuid;

  return host;
};

export const moveHostsAction = (
  state: SandboxState,
  input: Array<{
    id: number;
    coord_x?: number;
    coord_y?: number;
    group_id?: number;
    position?: number;
  }>
): SandboxHost[] => {
  input.forEach(({ id, group_id }) => {
    getHost(state, id);
    if (group_id && !state.hostGroups.some((group) => group.id === group_id)) {
      throw new SandboxDomainError('Группа хостов не найдена');
    }
  });
  return input.map(({ id, ...changes }) =>
    Object.assign(getHost(state, id), changes)
  );
};

const hostNumber = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
};

const validateHostInput = (
  state: SandboxState,
  input: SandboxHostInput
): {
  groupId: number;
  alias: string;
  type: string;
} => {
  const groupId = hostNumber(input.group_id, 0);
  if (!state.hostGroups.some((group) => group.id === groupId)) {
    throw new SandboxDomainError('Группа хостов не найдена');
  }
  const alias = String(input.alias ?? '').trim();
  if (!alias) throw new SandboxDomainError('Укажите название хоста');
  if (
    state.hosts.some((host) => host.alias.toLowerCase() === alias.toLowerCase())
  ) {
    throw new SandboxDomainError('Хост с таким названием уже существует');
  }
  const type = String(input.type ?? '').trim();
  if (!type) throw new SandboxDomainError('Укажите тип хоста');
  return { groupId, alias, type };
};

export const createHostAction = (
  state: SandboxState,
  input: SandboxHostInput
): SandboxHost => {
  const { groupId, alias, type } = validateHostInput(state, input);
  const id = Math.max(0, ...state.hosts.map((host) => host.id)) + 1;
  const position = hostNumber(input.position, id);
  let coordX = hostNumber(input.coord_x, 0);
  let coordY = hostNumber(input.coord_y, 0);
  if (input.coord_x == null || input.coord_y == null) {
    while (
      state.hosts.some(
        (host) => host.coord_x === coordX && host.coord_y === coordY
      )
    ) {
      coordX += 1;
      if (coordX > 99) {
        coordX = 0;
        coordY += 1;
      }
    }
  } else if (
    state.hosts.some(
      (host) => host.coord_x === coordX && host.coord_y === coordY
    )
  ) {
    throw new SandboxDomainError('Ячейка карты уже занята');
  }
  const host: SandboxHost = {
    __typename: 'HostOverview',
    origin: 'runtime',
    id,
    group_id: groupId,
    type,
    position,
    alias,
    coord_x: coordX,
    coord_y: coordY,
    in_service: false,
    online: true,
    online_booking_enabled: true,
    shell_mode: 'NORMAL',
    device_has_changed: false,
    locked: false,
    shell_client_uuid: null,
    mac_addr: '',
    ip_addr: '',
    qr: '',
    comment: '',
    info: { shell_version: null, processor: '', ram: '', video: '', disc: '' },
    counters: { active_window: 'SmartShell', disk_status: [] },
  };
  state.hosts.push(host);
  return host;
};

export const updateHostAction = (
  state: SandboxState,
  id: number,
  input: SandboxHostInput
): SandboxHost => {
  const host = getHost(state, id);
  const alias = String(input.alias ?? host.alias).trim();
  if (!alias) throw new SandboxDomainError('Укажите название хоста');
  if (
    state.hosts.some(
      (candidate) =>
        candidate.id !== id &&
        candidate.alias.toLowerCase() === alias.toLowerCase()
    )
  ) {
    throw new SandboxDomainError('Хост с таким названием уже существует');
  }
  const groupId = hostNumber(input.group_id, host.group_id);
  if (!state.hostGroups.some((group) => group.id === groupId)) {
    throw new SandboxDomainError('Группа хостов не найдена');
  }
  const coordX = hostNumber(input.coord_x, host.coord_x);
  const coordY = hostNumber(input.coord_y, host.coord_y);
  if (
    state.hosts.some(
      (candidate) =>
        candidate.id !== id &&
        candidate.coord_x === coordX &&
        candidate.coord_y === coordY
    )
  ) {
    throw new SandboxDomainError('Ячейка карты уже занята');
  }
  Object.assign(host, {
    alias,
    group_id: groupId,
    type: String(input.type ?? host.type),
    position: hostNumber(input.position, host.position),
    coord_x: coordX,
    coord_y: coordY,
  });
  return host;
};

export const createCommandAction = (
  state: SandboxState,
  input: {
    host_id?: number;
    host_ids?: number[];
    action?: string;
    param?: string;
  }
): { id: number; action: string; param: string } => {
  const ids = input.host_ids ?? (input.host_id ? [input.host_id] : []);
  if (!ids.length) throw new SandboxDomainError('Не выбраны хосты для команды');
  const hosts = ids.map((id) => getHost(state, id));
  const action = String(input.action ?? 'UNKNOWN');
  hosts.forEach((host) => {
    if (/wake/i.test(action)) host.online = true;
    if (/shutdown|poweroff/i.test(action)) host.online = false;
    if (/lock/i.test(action)) host.locked = true;
    if (/unlock/i.test(action)) host.locked = false;
  });
  return {
    id: state.nextCommandId++,
    action,
    param: String(input.param ?? ''),
  };
};
